import { Layout, Tag } from '@loadsmart/miranda-react'

import { AppointmentStatus } from '../../../appointments/ui/AppointmentStatus'
import { type Stop, getDelivery, getExtraStops, getPickup, isMultiStop } from '../../../domain/Stop'
import { formatType } from '../../../presenters/stopPresenter'
import { Information } from '../information/Information'
import { TimelineItem } from '../timeline/TimelineItem'
import { ExtraStopInformation } from './ExtraStopInformation'

export type StopsTimelineProps = {
  stops: Array<Stop>
  superTitleFormatter?: (stop: Stop) => string | undefined
  titleFormatter: (stop: Stop) => string
  subTitleFormatter?: (stop: Stop) => string | undefined
  compact?: boolean
  tags?: boolean
}

export function StopsTimeline({
  stops,
  superTitleFormatter,
  titleFormatter,
  subTitleFormatter,
  compact,
  tags,
  ...props
}: StopsTimelineProps) {
  const pickup = getPickup(stops)
  const delivery = getDelivery(stops)
  const hasExtraStops = isMultiStop(stops)

  let extraTimelineItems: React.ReactNode | Array<React.ReactNode>

  if (hasExtraStops) {
    const extraStops = getExtraStops(stops)

    if (compact) {
      extraTimelineItems = (
        <TimelineItem position="middle" dotless>
          <ExtraStopInformation
            stops={extraStops}
            titleFormatter={titleFormatter}
            subTitleFormatter={subTitleFormatter}
          />
        </TimelineItem>
      )
    } else {
      extraTimelineItems = extraStops.map((stop, index) => {
        const title = titleFormatter(stop)
        const superTitle = superTitleFormatter?.(stop)
        const subTitle = subTitleFormatter?.(stop)
        return (
          <TimelineItem key={`extra-stop-${title}`} position="middle">
            {tags && <Tag size="small">{formatType(index + 1, stops)}</Tag>}
            <Information title={title} superTitle={superTitle} subTitle={subTitle} />
            <AppointmentStatus appointment={stop.appointment} />
          </TimelineItem>
        )
      })
    }
  }

  return (
    <Layout.Stack gap="none" {...props}>
      <TimelineItem position="first">
        {tags && <Tag size="small">{formatType(0, stops)}</Tag>}
        <Information
          title={titleFormatter(pickup)}
          superTitle={superTitleFormatter?.(pickup)}
          subTitle={subTitleFormatter?.(pickup)}
        />
        <AppointmentStatus appointment={pickup.appointment} />
      </TimelineItem>
      {hasExtraStops && extraTimelineItems}
      <TimelineItem position="last">
        {tags && <Tag size="small">{formatType(stops.length - 1, stops)}</Tag>}
        <Information
          title={titleFormatter(delivery)}
          superTitle={superTitleFormatter?.(delivery)}
          subTitle={subTitleFormatter?.(delivery)}
        />
        <AppointmentStatus appointment={delivery.appointment} />
      </TimelineItem>
    </Layout.Stack>
  )
}
